import i18n from "./i18n/config";
import { getGameplayToken } from "./auth";

export interface ScoreEntry {
  player_id?: string;
  player_name: string;
  player_provider?: string | null;
  player_image?: string | null;
  completion_time_seconds: number;
  found_count: number;
  target_count: number;
  moves: Array<{
    title: string;
    duration_seconds: number;
    is_target: boolean;
  }>;
}

export interface ScoreboardResponse {
  date: string;
  language: string;
  scores: ScoreEntry[];
}

export interface ArchiveScoreboardResponse extends ScoreboardResponse {
  replay_scores: ScoreEntry[];
}

export interface CustomScoreboardResponse {
  public_id: string;
  language: string;
  scores: ScoreEntry[];
}

export interface CreatedCustomChallenge {
  public_id: string;
  path: string;
}

export type FeedbackKind = "feedback" | "bug";

export interface FeedbackRequest {
  kind: FeedbackKind;
  message: string;
  code?: string;
  context?: Record<string, string>;
  page?: string;
}

/** Thrown when a challenge can no longer be played (404/410). */
export class ChallengeUnavailableError extends Error {}

async function authenticatedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const token = await getGameplayToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(input, { ...init, headers });
}

/** Posts to a lobby-creating endpoint and returns the lobby id it redirects to. */
async function postForLobby(
  url: string,
  body?: URLSearchParams,
): Promise<string> {
  const response = await authenticatedFetch(url, {
    method: "POST",
    headers: body
      ? { "Content-Type": "application/x-www-form-urlencoded" }
      : undefined,
    body: body?.toString(),
    credentials: "include",
    redirect: "manual",
  });

  if (response.status === 0 || response.type === "opaqueredirect") {
    throw new Error(i18n.t("errors.notAuthenticated"));
  }
  if (response.status === 404 || response.status === 410) {
    throw new ChallengeUnavailableError(i18n.t("errors.challengeUnavailable"));
  }

  const redirectUrl =
    response.headers.get("HX-Redirect") || response.headers.get("Location");
  const match = redirectUrl?.match(/\/lobby\/(.+)$/);
  if (match) {
    return match[1];
  }
  throw new Error(i18n.t("errors.noLobbyId"));
}

export const api = {
  async createDailyChallenge(language = "en"): Promise<string> {
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await authenticatedFetch("/api/daily", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async createLobby(language = "en"): Promise<string> {
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await authenticatedFetch("/api/lobby/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async getScoreboard(
    language = "en",
    daysAgo = 0,
  ): Promise<ScoreboardResponse> {
    const response = await authenticatedFetch(
      `/api/daily/scoreboard?language=${language}&days_ago=${daysAgo}`,
      { credentials: "include" },
    );

    if (!response.ok) {
      throw new Error(i18n.t("errors.scoreboardFailed"));
    }

    return response.json();
  },

  /** Plays a past daily challenge; runs land on that day's "played later" board. */
  replayDaily(language: string, date: string): Promise<string> {
    return postForLobby(
      "/api/daily/replay",
      new URLSearchParams({ language, date }),
    );
  },

  playCustomChallenge(publicId: string): Promise<string> {
    return postForLobby(`/api/custom/${encodeURIComponent(publicId)}/play`);
  },

  async createCustomChallenge(
    language: string,
    startId: number,
    targetIds: number[],
  ): Promise<CreatedCustomChallenge> {
    const response = await authenticatedFetch("/api/custom", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        language,
        start_id: startId,
        target_ids: targetIds,
      }),
      credentials: "include",
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error ?? i18n.t("errors.createChallenge"));
    }
    return response.json();
  },

  /** Sends a message from the feedback panel to the maintainers' Discord. */
  async sendFeedback(feedback: FeedbackRequest): Promise<void> {
    // The token only attaches the display name, so feedback still goes
    // out anonymously when auth is down.
    const token = await getGameplayToken().catch(() => null);
    const response = await fetch("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(feedback),
    });
    if (!response.ok) {
      throw new Error(
        i18n.t(
          response.status === 429
            ? "components.feedback.rateLimited"
            : "components.feedback.error",
        ),
      );
    }
  },

  async getStreamer(): Promise<string | null> {
    const response = await fetch("/streamer");

    if (!response.ok) {
      return null;
    }

    const streamer = (await response.text()).trim();
    return streamer || null;
  },
};
